// Vendor Master — Phase 1E-δ Batch 1 (revisi redesign 2026-07-29)
// Vendor berlaku GLOBAL untuk semua region — tidak ada scoping region lagi.
// Role gating per role tuple backend:
//   VENDOR_WRITE_ROLES  = OWNER, KEPALA_CABANG   (pindah dari ADMIN_PURCHASING)
//   VENDOR_DELETE_ROLES = OWNER
import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { BusyButton } from "../../components/etams/BusyButton";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Truck, Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  listVendors, createVendor, updateVendor, deleteVendor, apiErrorMessage,
} from "../../lib/purchasingApi";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

//: Cermin `URUT_SAH_VENDOR` di backend/services/vendor_service.py.
const URUT_SAH = ["created_at", "name", "vendor_code", "payment_terms_days", "is_active"];
const KOLOM_CSV = [
  { kunci: "vendor_code", judul: "Kode" },
  { kunci: "name", judul: "Nama" },
  { kunci: "contact_person", judul: "Kontak" },
  { kunci: "phone", judul: "Telepon" },
  { kunci: "payment_terms_days", judul: "Termin (hari)" },
  { kunci: "is_active", judul: "Status", ambil: (v) => (v.is_active ? "Aktif" : "Nonaktif") },
];

const EMPTY_FORM = {
  vendor_code: "", name: "", contact_person: "", phone: "", email: "",
  address: "", npwp: "", bank_name: "", bank_account_no: "", bank_account_name: "",
  payment_terms_days: "", notes: "", is_active: true,
};

export default function Vendors() {
  const { roleCode, isOwner } = useAuth();
  const canWrite = ["OWNER", "KEPALA_CABANG"].includes(roleCode);
  const canDelete = isOwner;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // POLA DAFTAR SERAGAM (28 Sep 2026; docs/POLA_DAFTAR.md): cari/urut/halaman di
  // server + alamat halaman. Saringan bawaan "Aktif" terlihat sebagai chip dan bisa dihapus.
  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "name", arah: "asc" },
    saringan: {
      aktif: { label: "Status", param: "is_active", bawaan: "true",
               tampil: (v) => (v === "true" ? "Aktif" : "Nonaktif") },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((ovr = {}) => ({
    ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listVendors(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create; obj = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listVendors(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat daftar vendor."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      vendor_code: row.vendor_code || "",
      name: row.name || "",
      contact_person: row.contact_person || "",
      phone: row.phone || "",
      email: row.email || "",
      address: row.address || "",
      npwp: row.npwp || "",
      bank_name: row.bank_name || "",
      bank_account_no: row.bank_account_no || "",
      bank_account_name: row.bank_account_name || "",
      payment_terms_days: row.payment_terms_days ?? "",
      notes: row.notes || "",
      is_active: row.is_active ?? true,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.vendor_code.trim() || !form.name.trim()) {
      toast.error("Kode & nama vendor wajib diisi.");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form };
      body.payment_terms_days = body.payment_terms_days === ""
        ? null : Number(body.payment_terms_days);
      if (!editing) delete body.is_active;
      if (editing) {
        await updateVendor(editing.id, body);
        toast.success("Vendor berhasil diperbarui.");
      } else {
        await createVendor(body);
        toast.success("Vendor baru berhasil dibuat.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan vendor."));
    } finally {
      setSaving(false);
    }
  };

  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteVendor(confirmDelete.id);
      toast.success("Vendor dihapus.");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menghapus vendor."));
    } finally {
      setDeleting(false);
    }
  };

  const adaSaringan = !!daftar.cari.trim() || daftar.saringanAktif.length > 0;

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Truck className="h-6 w-6" /> Vendor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Master vendor untuk pengadaan bahan. Vendor berlaku untuk semua
            region. Dikelola oleh Owner dan Kepala Cabang.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="vendors-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          {canWrite && (
            <Button size="sm" onClick={openCreate} data-testid="vendors-create-btn">
              <Plus className="h-4 w-4 mr-1" /> Vendor Baru
            </Button>
          )}
        </div>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari kode atau nama vendor…" testId="vendors-search"
                    ekspor={{ nama: "vendor", kolom: KOLOM_CSV, ambilHalaman }}>
        <select
          value={daftar.nilaiSaringan.aktif}
          onChange={(e) => daftar.setSaringan("aktif", e.target.value)}
          className="h-9 rounded-md border bg-background px-2 text-sm"
          aria-label="Saring status vendor"
          data-testid="vendors-active-filter"
        >
          <option value="">Semua status</option>
          <option value="true">Aktif</option>
          <option value="false">Nonaktif</option>
        </select>
      </PalangDaftar>

      <Card className="overflow-hidden" data-testid="vendors-table">
        {loading ? (
          <div className="p-8 space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : daftar.tampil.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm space-y-3">
            {adaSaringan ? (
              <p>Tidak ada vendor yang cocok dengan pencarian atau saringan ini. Coba ubah kata cari atau hapus saringannya.</p>
            ) : (
              <>
                <p>Belum ada vendor terdaftar. Vendor adalah master pemasok bahan dan dikelola oleh Owner dan Kepala Cabang.</p>
                {canWrite ? (
                  <Button size="sm" onClick={openCreate} data-testid="vendors-empty-create">
                    <Plus className="h-4 w-4 mr-1" /> Vendor Baru
                  </Button>
                ) : (
                  <p>Minta Owner atau Kepala Cabang untuk menambahkan vendor.</p>
                )}
              </>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="vendor_code" className="px-4 py-3 font-medium">Kode</ThUrut>
                <ThUrut daftar={daftar} kunci="name" className="px-4 py-3 font-medium">Nama</ThUrut>
                <th className="px-4 py-3 font-medium">Kontak</th>
                <ThUrut daftar={daftar} kunci="payment_terms_days" className="px-4 py-3 font-medium">Termin</ThUrut>
                <ThUrut daftar={daftar} kunci="is_active" className="px-4 py-3 font-medium">Status</ThUrut>
                {canWrite && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {daftar.tampil.map((v) => (
                <tr key={v.id} className="border-b hover:bg-muted/50" data-testid={`vendor-row-${v.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{v.vendor_code}</td>
                  <td className="px-4 py-3">{v.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {v.contact_person || "-"}
                    {v.phone ? <span className="text-xs text-muted-foreground block">{v.phone}</span> : null}
                  </td>
                  <td className="px-4 py-3">
                    {v.payment_terms_days != null ? `${v.payment_terms_days} hari` : "-"}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge tone={v.is_active ? "success" : "neutral"}>
                      {v.is_active ? "Aktif" : "Nonaktif"}
                    </StatusBadge>
                  </td>
                  {canWrite && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(v)}
                              data-testid={`vendors-edit-${v.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {canDelete && (
                        <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(v)}
                                data-testid={`vendors-delete-${v.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paginasi daftar={daftar} testId="vendors-paginasi" />
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="vendor-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Vendor" : "Vendor Baru"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm">Kode Vendor <span aria-hidden="true">*</span></span>
                <Input value={form.vendor_code} data-testid="vendor-code"
                       onChange={(e) => setForm({ ...form, vendor_code: e.target.value })}
                       placeholder="Mis. VND-KAIN-001" />
              </label>
              <label className="block">
                <span className="text-sm">Nama Vendor <span aria-hidden="true">*</span></span>
                <Input value={form.name} data-testid="vendor-name"
                       onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm">Nama Kontak</span>
                <Input value={form.contact_person} data-testid="vendor-contact"
                       onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm">Telepon</span>
                <Input value={form.phone} data-testid="vendor-phone"
                       onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm">Email</span>
                <Input value={form.email} data-testid="vendor-email"
                       onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm">NPWP</span>
                <Input value={form.npwp} data-testid="vendor-npwp"
                       onChange={(e) => setForm({ ...form, npwp: e.target.value })} />
              </label>
            </div>

            <label className="block">
              <span className="text-sm">Alamat</span>
              <Textarea rows={2} value={form.address} data-testid="vendor-address"
                        onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </label>

            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <span className="text-sm">Bank</span>
                <Input value={form.bank_name} data-testid="vendor-bank"
                       onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm">No. Rekening</span>
                <Input value={form.bank_account_no} data-testid="vendor-bank-no"
                       onChange={(e) => setForm({ ...form, bank_account_no: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm">Atas Nama</span>
                <Input value={form.bank_account_name} data-testid="vendor-bank-name"
                       onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })} />
              </label>
            </div>

            <label className="block">
              <span className="text-sm">Termin Pembayaran (hari)</span>
              <Input type="number" min="0" value={form.payment_terms_days}
                     data-testid="vendor-terms"
                     onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} />
            </label>

            <label className="block">
              <span className="text-sm">Catatan</span>
              <Textarea rows={2} value={form.notes} data-testid="vendor-notes"
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </label>

            {editing && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_active}
                       data-testid="vendor-active"
                       onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                <span className="text-sm">Aktif</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Batal</Button>
            <BusyButton onClick={save} busy={saving} busyText="Menyimpan..."
                        className="bg-brand-orange text-brand-orange-foreground hover:bg-brand-orange/90"
                        data-testid="vendor-save-btn">
              Simpan
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent data-testid="vendor-delete-dialog">
          <DialogHeader>
            <DialogTitle>Hapus Vendor</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Hapus vendor <span className="font-medium">{confirmDelete?.name}</span> (
            <span className="font-mono text-xs">{confirmDelete?.vendor_code}</span>)?
            {/* Dulu berbunyi "tidak dapat dihapus oleh backend" — "backend"
                bahasa pengembang, dan daftarnya pun cuma menyebut PO padahal
                tagihan, retur, dan uang muka ikut menahan. PJ-04 langkah 7.

                ⚠ `{" "}` di bawah WAJIB: komentar JSX memakan spasi di
                sekitarnya, dan tanpa itu kalimatnya mendarat "…)?Vendor yang".
                Terlihat di layar, bukan di build — build hijau tanpa itu. */}
            {" "}Vendor yang sudah punya PO, tagihan, retur, atau uang muka tidak
            bisa dihapus — riwayatnya harus tetap utuh.{" "}
            <span className="font-medium">Nonaktifkan saja</span> kalau vendor
            ini sudah tidak dipakai lagi.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={deleting}>Batal</Button>
            <BusyButton variant="destructive" onClick={doDelete} busy={deleting} busyText="Menghapus..."
                        data-testid="vendor-delete-confirm">
              Hapus
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
